import React, { useState } from 'react';
import { Box, ClickAwayListener, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AvailableTimeWindow } from '../../types/appointment';
import useTranslation from '../../hooks/useTranslation';
import moment, { Moment } from 'moment';

interface DatePickerProps {
  availableWindows: AvailableTimeWindow[];
  selectedDate: string | null;
  onDateSelect: (date: string) => void;
  timezone: string;
}

const DatePickerComponent: React.FC<DatePickerProps> = ({
  availableWindows,
  selectedDate,
  onDateSelect,
  timezone,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const getAvailableDates = (): Moment[] => {
    const dates = new Set<string>();

    availableWindows.forEach(window => {
      const date = moment.utc(window.startTime).tz(timezone);
      const dateString = date.format('YYYY-MM-DD');
      dates.add(dateString);
    });

    return Array.from(dates)
      .sort()
      .map(dateString => moment(dateString));
  };

  const shouldDisableDate = (date: Moment): boolean => {
    const dateString = date.format('YYYY-MM-DD');
    const availableDates = getAvailableDates();
    return !availableDates.some(
      availableDate => availableDate.format('YYYY-MM-DD') === dateString
    );
  };

  const handleDateChange = (date: Moment | null) => {
    if (date) {
      const dateString = date.format('YYYY-MM-DD');
      onDateSelect(dateString);
    }
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const selectedDateValue = selectedDate ? moment(selectedDate) : null;

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        {t('contact.booking.selectDate')}
      </Typography>
      <ClickAwayListener onClickAway={handleClose}>
        <Box>
          <DatePicker
            open={open}
            onOpen={handleOpen}
            onClose={handleClose}
            closeOnSelect
            value={selectedDateValue}
            onChange={handleDateChange}
            shouldDisableDate={shouldDisableDate}
            slotProps={{
              textField: {
                fullWidth: true,
                placeholder: t('contact.booking.selectDate'),
                onClick: handleOpen,
              },
            }}
          />
        </Box>
      </ClickAwayListener>
    </Box>
  );
};

export default DatePickerComponent;
